import { isEmpty, isObjectLike, clone, find, last, isUndefined } from 'lodash';

import * as Constants from 'commons-vehicle/constants/apiDataConversionConstants.js';
import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import { IMAGE_STATUS } from 'commons-vehicle/ModelYear/constants/exteriorColourStatuses.js';
import PURCHASE_METHODS_VALUES from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';
import YearModel from 'commons-vehicle/ModelYear/models/YearModel.js';
import * as PurchaseMethodsService from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';

function resetYearFromCollectionData(collectionData, year, disclaimers) {
    const exteriorColours = {
        photo1: '',
        photo2: '',
        photo3: '',
    };
    let exteriorColourStatus = 0;

    if (collectionData[Constants.VIEW_1.VEHICLE.HIGH_PRICE]) {
        const highPriceObject =
            collectionData[Constants.VIEW_1.VEHICLE.HIGH_PRICE][Constants.VIEW_1.VEHICLE.HIGH_PRICE_INDEX];
        exteriorColours.photo1 =
            highPriceObject[Constants.VIEW_1.VEHICLE.URL_EXTERIOR_COLOUR][Constants.VIEW_1.COLOUR_IMAGE.PHOTO_1];
        exteriorColours.photo2 =
            highPriceObject[Constants.VIEW_1.VEHICLE.URL_EXTERIOR_COLOUR][Constants.VIEW_1.COLOUR_IMAGE.PHOTO_2];
        exteriorColours.photo3 =
            highPriceObject[Constants.VIEW_1.VEHICLE.URL_EXTERIOR_COLOUR][Constants.VIEW_1.COLOUR_IMAGE.PHOTO_3];

        if (!isUndefined(highPriceObject[Constants.VIEW_1.VEHICLE.EXTERIOR_COLOUR_STATUS])) {
            exteriorColourStatus = highPriceObject[Constants.VIEW_1.VEHICLE.EXTERIOR_COLOUR_STATUS];
        } else if (exteriorColours.photo1) {
            exteriorColourStatus = IMAGE_STATUS.FULL_COLORIZED;
        }
    }

    let highRebate = 0;
    let highRebatePurchaseMethod = PURCHASE_METHODS_VALUES.CASH;
    if (collectionData[Constants.VIEW_1.VEHICLE.HIGH_REBATE]) {
        const highRebateObject =
            collectionData[Constants.VIEW_1.VEHICLE.HIGH_REBATE][Constants.VIEW_1.VEHICLE.HIGH_REBATE_INDEX];
        const highRebateTaxes = highRebateObject[Constants.VIEW_1.VEHICLE.HIGH_REBATE_REBATES_TAXES] || 0;
        highRebate = highRebateObject[Constants.VIEW_1.VEHICLE.HIGH_REBATE_RETAIL] + highRebateTaxes;
        highRebatePurchaseMethod = highRebateObject[Constants.VIEW_1.VEHICLE.HIGH_REBATE_PURCHASE_METHOD];
    }

    const modelTrimPurchaseMethodIds = [];

    if (
        collectionData[Constants.VIEW_1.VEHICLE.PRICE] &&
        collectionData[Constants.VIEW_1.VEHICLE.PRICE][Constants.VIEW_1.VEHICLE.PRICE_INDEX]
    ) {
        const price = collectionData[Constants.VIEW_1.VEHICLE.PRICE][Constants.VIEW_1.VEHICLE.PRICE_INDEX];
        modelTrimPurchaseMethodIds.push({
            purchaseMethod: PURCHASE_METHODS_VALUES.CASH,
            modelTrimCode: price[Constants.VIEW_1.VEHICLE.PRICE_MODEL_TRIM_CODE],
            configurationId: price[Constants.VIEW_1.VEHICLE.PRICE_CONFIGURATION_ID],
        });
    }

    if (
        collectionData[Constants.VIEW_1.VEHICLE.FINANCE] &&
        collectionData[Constants.VIEW_1.VEHICLE.FINANCE][Constants.VIEW_1.VEHICLE.FINANCE_INDEX]
    ) {
        const finance = collectionData[Constants.VIEW_1.VEHICLE.FINANCE][Constants.VIEW_1.VEHICLE.FINANCE_INDEX];
        modelTrimPurchaseMethodIds.push({
            purchaseMethod: PURCHASE_METHODS_VALUES.FINANCE,
            modelTrimCode: finance[Constants.VIEW_1.VEHICLE.FINANCE_MODEL_TRIM_CODE],
            configurationId: finance[Constants.VIEW_1.VEHICLE.FINANCE_CONFIGURATION_ID],
        });
    }

    if (
        collectionData[Constants.VIEW_1.VEHICLE.LEASE] &&
        collectionData[Constants.VIEW_1.VEHICLE.LEASE][Constants.VIEW_1.VEHICLE.LEASE_INDEX]
    ) {
        const lease = collectionData[Constants.VIEW_1.VEHICLE.LEASE][Constants.VIEW_1.VEHICLE.LEASE_INDEX];
        modelTrimPurchaseMethodIds.push({
            purchaseMethod: PURCHASE_METHODS_VALUES.LEASE,
            modelTrimCode: lease[Constants.VIEW_1.VEHICLE.LEASE_MODEL_TRIM_CODE],
            configurationId: lease[Constants.VIEW_1.VEHICLE.LEASE_CONFIGURATION_ID],
        });
    }

    return new YearModel({
        year,
        make: collectionData.make,
        model: collectionData.model,
        bodyStyleCode: collectionData.bodyStyleCode,
        bodyStyle: collectionData.bodyStyle[Constants.DESCRIPTION_LANGUAGE_INDEX],
        modelTrimPurchaseMethodIds,
        exteriorColours,
        exteriorColourStatus,
        highRebate,
        highRebatePurchaseMethod,
        purchaseMethods: PurchaseMethodsService.resetPurchaseMethodsFromOriginData(
            DataOrigins.MODELS_COLLECTION,
            collectionData,
            undefined,
            disclaimers
        ),
    });
}

export function resetYearFromOriginData(dataOrigin, originYearData, year, disclaimers) {
    if (isEmpty(originYearData) || !isObjectLike(originYearData)) {
        return new YearModel();
    }

    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            return resetYearFromCollectionData(originYearData, year, disclaimers);

        case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
            return new YearModel();

        default:
            return new YearModel();
    }
}

export function resetYearPurchaseMethod(year, purchaseMethod) {
    return year.set(
        'purchaseMethods',
        PurchaseMethodsService.selectPurchaseMethod(year.purchaseMethods, purchaseMethod)
    );
}

export function recalculateYearPayments(year, paymentCalculationParameters) {
    let newPaymentCalculationParameters = paymentCalculationParameters;
    let selectedLeaseTerm;
    let selectedFinanceTerm;
    if (
        !find(year.purchaseMethods.lease.terms, (term) => term.term === paymentCalculationParameters.selectedLeaseTerm)
    ) {
        selectedLeaseTerm = last(year.purchaseMethods.lease.terms).term;
    }
    if (
        !find(
            year.purchaseMethods.finance.terms,
            (term) => term.term === paymentCalculationParameters.selectedFinanceTerm
        )
    ) {
        selectedFinanceTerm = last(year.purchaseMethods.finance.terms).term;
    }
    if (selectedLeaseTerm || selectedFinanceTerm) {
        newPaymentCalculationParameters = clone(paymentCalculationParameters);
        newPaymentCalculationParameters.selectedLeaseTerm =
            selectedLeaseTerm || newPaymentCalculationParameters.selectedLeaseTerm;
        newPaymentCalculationParameters.selectedFinanceTerm =
            selectedFinanceTerm || newPaymentCalculationParameters.selectedFinanceTerm;
    }

    return year.set(
        'purchaseMethods',
        PurchaseMethodsService.recalculatePayments(year.purchaseMethods, newPaymentCalculationParameters)
    );
}

export function recalculateYearPurchaseAmounts(year, paymentCalculationParameters) {
    const newYear = year.set(
        'purchaseMethods',
        PurchaseMethodsService.recalculatePurchaseAmounts(year.purchaseMethods, paymentCalculationParameters)
    );

    return recalculateYearPayments(newYear, paymentCalculationParameters);
}

export default {
    resetYearFromOriginData,
    resetYearPurchaseMethod,
    recalculateYearPayments,
    recalculateYearPurchaseAmounts,
};



// WEBPACK FOOTER //
// ../widget-commons-vehicle/src/ModelYear/services/YearService/YearService.js